import { useCallback, useEffect, useState } from 'react';
import {
  createOptionsAlertRule,
  deleteOptionsAlertRule,
  fetchOptionsAlerts,
  markOptionsAlertRead,
  toggleOptionsAlertRule,
  updateOptionsAlertRule,
  updateOptionsAlertsPreferences,
} from '../lib/optionsApi';

export const useOptionsAlertsData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyRuleId, setBusyRuleId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const next = await fetchOptionsAlerts();
      setData(next);
      return next;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load alerts');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runMutation = useCallback(async (ruleId, task) => {
    try {
      setBusyRuleId(ruleId || 'global');
      await task();
      await load();
      return true;
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Alert action failed');
      return false;
    } finally {
      setBusyRuleId(null);
    }
  }, [load]);

  return {
    data,
    loading,
    error,
    busyRuleId,
    reload: load,
    createRule: (payload) => runMutation(null, () => createOptionsAlertRule(payload)),
    updateRule: (ruleId, payload) => runMutation(ruleId, () => updateOptionsAlertRule(ruleId, payload)),
    toggleRule: (ruleId, enabled) => runMutation(ruleId, () => toggleOptionsAlertRule(ruleId, enabled)),
    deleteRule: (ruleId) => runMutation(ruleId, () => deleteOptionsAlertRule(ruleId)),
    updatePreferences: (payload) => runMutation(null, () => updateOptionsAlertsPreferences(payload)),
    markRead: (eventId) => runMutation(eventId, () => markOptionsAlertRead(eventId)),
  };
};

export default useOptionsAlertsData;